import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { Loader2, Calendar as CalendarIcon, Search } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { cn } from '../lib/utils';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScorecardsListPage() {
  const [loading, setLoading] = useState(true);
  const [scorecards, setScorecards] = useState([]);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    fetchScorecards();
  }, [dateRange]);

  const fetchScorecards = async () => {
    try {
      const params = {};
      if (dateRange.from) {
        params.start_date = format(dateRange.from, 'yyyy-MM-dd');
      }
      if (dateRange.to) {
        params.end_date = format(dateRange.to, 'yyyy-MM-dd');
      }
      
      const res = await axios.get(`${API}/scorecards`, { params });
      setScorecards(res.data);
    } catch (error) {
      console.error('Failed to fetch scorecards:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setDateRange({ from: null, to: null });
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="scorecards-list">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
            My Scorecards
          </h1>
          <p className="text-slate-600 mt-1">View your daily performance history</p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !dateRange.from && "text-muted-foreground"
                    )}
                    data-testid="date-filter-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "LLL dd")} - {format(dateRange.to, "LLL dd, y")}
                        </>
                      ) : (
                        format(dateRange.from, "LLL dd, y")
                      )
                    ) : (
                      <span>Filter by date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange(range || { from: null, to: null });
                      if (range?.to) setDatePickerOpen(false);
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>

              {(dateRange.from || dateRange.to) && (
                <Button variant="ghost" onClick={clearFilters} className="text-slate-500">
                  Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Scorecards List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {scorecards.length} Scorecard{scorecards.length !== 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : scorecards.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No scorecards found</p>
                <Button asChild className="mt-4">
                  <Link to="/entry">Create your first entry</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {scorecards.map((sc, index) => (
                  <Link 
                    key={sc.entry_id}
                    to={`/scorecards/${sc.entry_id}`}
                    className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-colors group"
                    data-testid={`scorecard-row-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-mono font-bold text-lg text-white ${
                        sc.overall_score >= 85 ? 'bg-emerald-500' :
                        sc.overall_score >= 70 ? 'bg-lime-500' :
                        sc.overall_score >= 55 ? 'bg-amber-500' : 'bg-red-500'
                      }`}>
                        {Math.round(sc.overall_score)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 group-hover:text-violet-700 transition-colors">
                          {formatDate(sc.date)}
                        </p>
                        <p className="text-sm text-slate-500">{sc.location_name}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Badge 
                        variant="outline"
                        className={cn(
                          sc.status === 'final' 
                            ? 'border-emerald-500 text-emerald-700 bg-emerald-50' 
                            : 'border-amber-500 text-amber-700 bg-amber-50'
                        )}
                      >
                        {sc.status === 'final' ? 'Final' : 'Preliminary'}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
